import { portalText } from "../../i18n/portal.js";
/**
 * Area `postpaid` of the console gateway — over `apps/api/src/routes/postpaid/` (design §27.9.2). Owned by plan-014
 * unit **E5**.
 *
 *   readPostpaid(billingAccountId)              `getPostpaid`     the contract, owed now (billed + not billed yet),
 *                                                                 still available, the terms and the schedule, the
 *                                                                 open invoices with their steps, the last settled
 *   payPostpaid(billingAccountId, closeId)      `payPostpaid`     pay an open invoice by card: the processor's page
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in E5's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated by
 * the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through {@link operationsOf}, a
 * structural view of the client with exactly these methods (D5a's and D5b's pattern): it compiles before and after the
 * regeneration, and a client that still lacks one raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { operationPaymentOfWire, type OperationPaymentView } from "./billing-operation.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────────

export type PostpaidActionView = "charge" | "reminder" | "banner" | "suspend";
export type CollectionStateView = "current" | "grace" | "suspended_nonpayment";

export interface PostpaidScheduleStepView {
  /** Days after the invoice's due date. */
  readonly day: number;
  readonly action: readonly PostpaidActionView[];
}

export interface PostpaidContractView {
  readonly postpaidContractId: string;
  readonly creditLimitMinor: number;
  readonly permittedOverageMinor: number;
  readonly paymentTermDay: number;
  readonly collectionMethod: readonly ("card_payment_link" | "bank_transfer" | "mandate")[];
  readonly activeFrom: string;
  readonly schedule: readonly PostpaidScheduleStepView[];
}

export interface PostpaidStepView {
  readonly dayOffset: number;
  readonly action: readonly PostpaidActionView[];
  readonly dueAt: string;
  readonly state: "scheduled" | "done" | "skipped";
}

export interface PostpaidInvoiceView {
  readonly billingPeriodCloseId: string;
  readonly yearMonth: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly netDueMinor: number;
  readonly taxMinor: number;
  readonly grossDueMinor: number;
  readonly currency: string;
  readonly dueAt: string;
  readonly overdue: boolean;
  readonly paymentState: "nothing_due" | "unpaid" | "paid";
  readonly settledAt: string | null;
  readonly settledBy: "card" | "bank_transfer" | null;
  readonly invoiceDocumentId: string | null;
  readonly paymentPending: boolean;
  readonly step: readonly PostpaidStepView[];
}

export interface PostpaidView {
  readonly billingAccountId: string;
  readonly billingAccountName: string | null;
  readonly collectionPolicy: "prepaid" | "postpaid";
  readonly collectionState: CollectionStateView;
  readonly currency: string;
  readonly billingTimeZone: string;
  readonly contract: PostpaidContractView | null;
  readonly availableMinor: number;
  readonly stillAvailableMinor: number;
  readonly owedMinor: { readonly billed: number; readonly notBilled: number; readonly total: number };
  readonly openInvoice: readonly PostpaidInvoiceView[];
  readonly lastSettled: PostpaidInvoiceView | null;
  readonly nextCloseAt: string;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** One balance's postpaid facts (the contract is `null` for a balance that pays in advance). */
    readPostpaid(billingAccountId: string): Promise<PostpaidView>;
    /** Pays an open invoice (the oldest when `billingPeriodCloseId` is `null`) by card; the answer names the page. */
    payPostpaid(billingAccountId: string, billingPeriodCloseId: string | null): Promise<OperationPaymentView>;
  }
}

export type PostpaidGatewaySlice = Pick<ConsoleGateway, "readPostpaid" | "payPostpaid">;

/** This area's rows of `GATEWAY_GAP`: none — both operations are in E5's fragments. */
export const postpaidGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────────

interface PostpaidOperations {
  getPostpaid(request: { path: { billing_account_id: string } }): Promise<ApiResult>;
  payPostpaid(request: { path: { billing_account_id: string }; body?: unknown; header?: Readonly<Record<string, string>> }): Promise<ApiResult>;
}

function operationsOf(client: WeTalkApiClient): PostpaidOperations {
  const held = client as unknown as Partial<Record<keyof PostpaidOperations, unknown>>;
  return new Proxy({} as PostpaidOperations, {
    get(_target, name: string) {
      const found = held[name as keyof PostpaidOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_fa4f75f0", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new Error(`${path}: ${JSON.stringify(value)} is not one of ${allowed.join(", ")}`);
  return found;
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function integerOf(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`${path} is not a whole number`);
  return value;
}

const ACTION: readonly PostpaidActionView[] = ["charge", "reminder", "banner", "suspend"];
const COLLECTION_STATE: readonly CollectionStateView[] = ["current", "grace", "suspended_nonpayment"];

function actionsOf(value: unknown, path: string): readonly PostpaidActionView[] {
  return readArray(value, path).map((one, index) => oneOf(ACTION, one, `${path}[${String(index)}]`));
}

function invoiceOf(value: unknown, path: string): PostpaidInvoiceView {
  const one = readRecord(value, path);
  const settledBy = one["settled_by"];
  return {
    billingPeriodCloseId: readText(one["billing_period_close_id"], `${path}.billing_period_close_id`),
    yearMonth: readText(one["year_month"], `${path}.year_month`),
    periodStart: readText(one["period_start"], `${path}.period_start`),
    periodEnd: readText(one["period_end"], `${path}.period_end`),
    netDueMinor: readWholeNumber(one["net_due_minor"], `${path}.net_due_minor`),
    taxMinor: readWholeNumber(one["tax_minor"], `${path}.tax_minor`),
    grossDueMinor: readWholeNumber(one["gross_due_minor"], `${path}.gross_due_minor`),
    currency: readText(one["currency"], `${path}.currency`),
    dueAt: readText(one["due_at"], `${path}.due_at`),
    overdue: readBoolean(one["overdue"], `${path}.overdue`),
    paymentState: oneOf(["nothing_due", "unpaid", "paid"] as const, one["payment_state"], `${path}.payment_state`),
    settledAt: textOrNull(one["settled_at"], `${path}.settled_at`),
    settledBy: settledBy === null || settledBy === undefined ? null : oneOf(["card", "bank_transfer"] as const, settledBy, `${path}.settled_by`),
    invoiceDocumentId: textOrNull(one["invoice_document_id"], `${path}.invoice_document_id`),
    paymentPending: one["payment_pending"] === undefined ? false : readBoolean(one["payment_pending"], `${path}.payment_pending`),
    step:
      one["step"] === undefined
        ? []
        : readArray(one["step"], `${path}.step`).map((raw, index) => {
            const step = readRecord(raw, `${path}.step[${index}]`);
            return {
              dayOffset: readWholeNumber(step["day_offset"], `${path}.step[${index}].day_offset`),
              action: actionsOf(step["action"], `${path}.step[${index}].action`),
              dueAt: readText(step["due_at"], `${path}.step[${index}].due_at`),
              state: oneOf(["scheduled", "done", "skipped"] as const, step["state"], `${path}.step[${index}].state`),
            };
          }),
  };
}

function contractOf(value: unknown, path: string): PostpaidContractView | null {
  if (value === null || value === undefined) return null;
  const one = readRecord(value, path);
  return {
    postpaidContractId: readText(one["postpaid_contract_id"], `${path}.postpaid_contract_id`),
    creditLimitMinor: readWholeNumber(one["credit_limit_minor"], `${path}.credit_limit_minor`),
    permittedOverageMinor: readWholeNumber(one["permitted_overage_minor"], `${path}.permitted_overage_minor`),
    paymentTermDay: readWholeNumber(one["payment_term_day"], `${path}.payment_term_day`),
    collectionMethod: readArray(one["collection_method"], `${path}.collection_method`).map((raw, index) =>
      oneOf(["card_payment_link", "bank_transfer", "mandate"] as const, raw, `${path}.collection_method[${index}]`),
    ),
    activeFrom: readText(one["active_from"], `${path}.active_from`),
    schedule: readArray(one["schedule"], `${path}.schedule`).map((raw, index) => {
      const step = readRecord(raw, `${path}.schedule[${index}]`);
      return { day: readWholeNumber(step["day"], `${path}.schedule[${index}].day`), action: actionsOf(step["action"], `${path}.schedule[${index}].action`) };
    }),
  };
}

/** The answer of `getPostpaid`, decoded (a shape the console does not know raises, never renders half). */
export function postpaidOfWire(data: Readonly<Record<string, unknown>>, path: string): PostpaidView {
  const owed = readRecord(data["owed_minor"], `${path}.owed_minor`);
  return {
    billingAccountId: readText(data["billing_account_id"], `${path}.billing_account_id`),
    billingAccountName: textOrNull(data["billing_account_name"], `${path}.billing_account_name`),
    collectionPolicy: oneOf(["prepaid", "postpaid"] as const, data["collection_policy"], `${path}.collection_policy`),
    collectionState: oneOf(COLLECTION_STATE, data["collection_state"], `${path}.collection_state`),
    currency: readText(data["currency"], `${path}.currency`),
    billingTimeZone: readText(data["billing_timezone"], `${path}.billing_timezone`),
    contract: contractOf(data["contract"], `${path}.contract`),
    availableMinor: integerOf(data["available_minor"], `${path}.available_minor`),
    stillAvailableMinor: integerOf(data["still_available_minor"], `${path}.still_available_minor`),
    owedMinor: {
      billed: readWholeNumber(owed["billed"], `${path}.owed_minor.billed`),
      notBilled: readWholeNumber(owed["not_billed"], `${path}.owed_minor.not_billed`),
      total: readWholeNumber(owed["total"], `${path}.owed_minor.total`),
    },
    openInvoice: readArray(data["open_invoice"], `${path}.open_invoice`).map((one, index) => invoiceOf(one, `${path}.open_invoice[${index}]`)),
    lastSettled: data["last_settled"] === null || data["last_settled"] === undefined ? null : invoiceOf(data["last_settled"], `${path}.last_settled`),
    nextCloseAt: readText(data["next_close_at"], `${path}.next_close_at`),
  };
}

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  const body = readRecord(assertOk(result), operationId);
  return readRecord(body["data"], `${operationId}.data`);
}

/** A fresh Idempotency-Key for one "Pay" press (16–128 characters). */
function idempotencyKeyOf(): string {
  return `postpaid-pay-${globalThis.crypto.randomUUID()}`;
}

export function postpaidApiSlice(client: WeTalkApiClient, _context: SliceContext): PostpaidGatewaySlice {
  const operations = operationsOf(client);
  return {
    async readPostpaid(billingAccountId) {
      const result = await operations.getPostpaid({ path: { billing_account_id: billingAccountId } });
      return postpaidOfWire(dataOf(result, "getPostpaid"), "getPostpaid");
    },
    async payPostpaid(billingAccountId, billingPeriodCloseId) {
      const result = await operations.payPostpaid({
        path: { billing_account_id: billingAccountId },
        body: billingPeriodCloseId === null ? {} : { billing_period_close_id: billingPeriodCloseId },
        header: { "idempotency-key": idempotencyKeyOf() },
      });
      return operationPaymentOfWire(dataOf(result, "payPostpaid"), "payPostpaid");
    },
  };
}
